import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../../ui/chart';
import { Pie, PieChart, Cell } from 'recharts';
import { CampaignReportData } from '../../../types/campaignReport';
import { formatPercent, formatCurrencyGBP } from '../../../lib/reportFormatters';

interface RetentionTabProps {
  data: CampaignReportData;
}

const retentionChartConfig: ChartConfig = {
  retained: {
    label: 'Retained',
    color: '#183df7',
  },
  churned: {
    label: 'Churned',
    color: '#ef4444',
  },
};

const autoRenewChartConfig: ChartConfig = {
  autoRenew: {
    label: 'Auto-Renew',
    color: '#4a6cf7',
  },
  manual: {
    label: 'Manual',
    color: '#94a3b8',
  },
};

export function RetentionTab({ data }: RetentionTabProps) {
  const completionRate = data.retention.threeMonthCompletionRate;
  const churnRate = data.retention.churnRate;
  const autoRenewTakeUp = data.retention.autoRenewTakeUp || 0;
  
  const retentionData = [
    { name: 'Retained', value: completionRate, color: '#183df7' },
    { name: 'Churned', value: churnRate, color: '#ef4444' },
  ];
  
  const autoRenewData = autoRenewTakeUp > 0 ? [
    { name: 'Auto-Renew', value: autoRenewTakeUp, color: '#4a6cf7' },
    { name: 'Manual', value: 100 - autoRenewTakeUp, color: '#94a3b8' },
  ] : null;

  return (
    <div className="space-y-8">
      {/* Retention & Churn Table */}
      <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
        <CardHeader>
          <CardTitle className="text-xl font-bold" style={{ color: '#183df7' }}>
            5️⃣ Retention & Churn
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">Metric</TableHead>
                <TableHead className="text-right font-semibold">Result</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">3-Month Completion Rate</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(completionRate)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Churn Rate</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatPercent(churnRate)}
                </TableCell>
              </TableRow>
              {autoRenewTakeUp > 0 && (
                <TableRow>
                  <TableCell className="font-medium">Auto-Renew Take-Up (Month 4+)</TableCell>
                  <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                    {formatPercent(autoRenewTakeUp)} of retained base
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className="font-medium">Average Lifetime Value per Customer</TableCell>
                <TableCell className="text-right font-semibold" style={{ color: '#183df7' }}>
                  {formatCurrencyGBP(data.retention.averageLifetimeValue)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ color: '#183df7' }}>
              Retention vs Churn
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={retentionChartConfig} className="h-[250px]">
              <PieChart>
                <Pie
                  data={retentionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {retentionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <ChartTooltip content={<ChartTooltipContent formatter={(value: any) => formatPercent(Array.isArray(value) ? value[0] : value)} />} />
              </PieChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {autoRenewData && (
          <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
            <CardHeader>
              <CardTitle className="text-lg font-semibold" style={{ color: '#183df7' }}>
                Auto-Renew Take-Up
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer config={autoRenewChartConfig} className="h-[250px]">
                <PieChart>
                  <Pie
                    data={autoRenewData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {autoRenewData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent formatter={(value: any) => formatPercent(Array.isArray(value) ? value[0] : value)} />} />
                </PieChart>
              </ChartContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
